<template>
  <div v-if="show" class="download-modal-overlay" @click="closeModal">
    <div
      class="download-modal-popover"
      :style="popoverStyle"
      @click.stop
    >
      <div class="popover-header">
        <h4 class="modal-title">{{ modalTitle }}</h4>
        <button
          type="button"
          class="close"
          @click="closeModal"
          aria-label="Close"
        >
          <span class="material-symbols-outlined close-button-icon">
            close
          </span>
        </button>
      </div>

      <div class="download-invoice-modal">
        <b-form-group>
          <label class="form__label">{{ templateTypeLabel }}</label>
          <multiselect
            v-model="selectedTemplate"
            :options="templates"
            :placeholder="'Select template type'"
            :track-by="'id'"
            :label="'name'"
            :allow-empty="false"
            :showLabels="false"
          />
        </b-form-group>

        <b-button
          type="button"
          @click="downloadInvoice"
          class="button"
          variant="primary"
          :disabled="!selectedTemplate"
        >
          Download
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import multiselect from 'vue-multiselect'
import { suppressBeforeUnloadForDownload } from '@mixins/invoices/InvoiceEditConfirmTabCloseMixin'

export default {
  name: 'DownloadInvoiceModal',
  components: {
    multiselect
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    invoice: {
      type: Object,
      default: null
    },
    templates: {
      type: Array,
      default: () => []
    },
    triggerElement: {
      type: [Object, HTMLElement],
      default: null
    },
    documentType: {
      type: String,
      default: 'Invoice'
    },
    numberField: {
      type: String,
      default: 'invoice_number'
    }
  },
  data () {
    return {
      selectedTemplate: null
    }
  },
  computed: {
    show: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    modalTitle () {
      const number = this.invoice?.[this.numberField]
      return number ? `${this.documentType} ${number}` : this.documentType
    },
    templateTypeLabel () {
      return `${this.documentType} template type`
    },
    popoverStyle () {
      if (!this.triggerElement) {
        return {
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          minWidth: '400px'
        }
      }

      const rect = this.triggerElement.getBoundingClientRect()
      return {
        position: 'fixed',
        top: `${rect.bottom + 8}px`,
        right: `${window.innerWidth - rect.right}px`,
        minWidth: '400px'
      }
    }
  },
  watch: {
    value (newValue) {
      if (newValue && this.invoice && this.templates.length > 0) {
        // Set default template to current invoice template
        if (this.invoice.invoice_template_type) {
          // Find the template object that matches the invoice's template type
          const matchingTemplate = this.templates.find(template =>
            template.id === this.invoice.invoice_template_type.id
          )
          this.selectedTemplate = matchingTemplate || this.templates[0]
        } else {
          // Fallback to first template if no invoice template type
          this.selectedTemplate = this.templates[0]
        }
      }
    },
    // Also watch templates array to handle when templates load after modal opens
    templates (newTemplates) {
      if (this.value && this.invoice && newTemplates.length > 0 && !this.selectedTemplate) {
        if (this.invoice.invoice_template_type) {
          const matchingTemplate = newTemplates.find(template =>
            template.id === this.invoice.invoice_template_type.id
          )
          this.selectedTemplate = matchingTemplate || newTemplates[0]
        } else {
          this.selectedTemplate = newTemplates[0]
        }
      }
    }
  },
  methods: {
    downloadInvoice () {
      if (!this.selectedTemplate || !this.invoice || !this.invoice.download_url) {
        return
      }

      // Construct download URL with template parameter
      const templateName = this.selectedTemplate.name
      const baseUrl = this.invoice.download_url
      const downloadUrl = templateName !== this.invoice.invoice_template_type?.name
        ? `${baseUrl}/${templateName}`
        : baseUrl

      // Suppress the "Leave site?" beforeunload dialog while the download triggers
      suppressBeforeUnloadForDownload()

      // Trigger download without page navigation to avoid "Leave site?" dialog
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = ''
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // Close modal after download
      this.closeModal()
    },
    closeModal () {
      this.show = false
      this.selectedTemplate = null
    }
  }
}
</script>

<style lang="scss" scoped>
.download-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1050;
}

.download-modal-popover {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 1051;
  overflow: visible;
}

.popover-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 20px 0 20px;
  border-bottom: none;
  background-color: transparent;
}

.modal-title {
  margin: 0;
  font-size: 15px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
}

.button {
  margin-top: 20px;
}

.close {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;

  .close-button-icon {
    font-size: 24px;
    color: #6c757d;

    &:hover {
      color: #000;
    }
  }
}

.download-invoice-modal {
  padding: 1.25rem;
}

.form__label {
  color: $inputLabelColor1;
  font-size: 12px;
  line-height: 16px;
}

// Ensure multiselect dropdown appears above the popover
:deep(.multiselect__content-wrapper) {
  z-index: 1052;
  position: absolute;
}

:deep(.multiselect__content) {
  background: white;
  border: 1px solid #e1e5e9;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
</style>
